import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { IPlayer, IPlayerGameStats, IPlayerSeasonStats, ITeamInfo } from "../../../types/types";
import { useLocalization } from "../../../hooks/useLocalization";
import { useLocalePath } from "../../../hooks/useLocalePath";
import { useTeamInfo } from "../../../hooks/useTeams";
import {
  fetchPlayerGameStats,
  fetchPlayerSeasonStats,
  fetchSeasons,
} from "../../../services/services";
import {
  findAllPlayerSeasonStats,
  normalizePlayerName,
  playerLookupNames,
} from "../../../context/seasonStats";
import { aggregateChampionStats } from "../../../utils/championStats";
import { hasMultikillData } from "../../../utils/multikill";
import { sortGamesChronologically } from "../../../utils/playerGameSort";
import { MOBILE_CHAMP_PREVIEW, rankedCrestUrl } from "../constants";

const findRosterPlayer = (
  teamPlayers: IPlayer[] | null | undefined,
  decodedName: string,
): IPlayer | undefined =>
  teamPlayers?.find(
    (player) =>
      normalizePlayerName(player.name) === normalizePlayerName(decodedName) ||
      normalizePlayerName(player.displayName) === normalizePlayerName(decodedName),
  );

const findRosterPlayerOnAnyTeam = (
  teams: ITeamInfo[] | undefined,
  decodedName: string,
): { player: IPlayer; teamName: string } | undefined => {
  for (const team of teams ?? []) {
    const player = findRosterPlayer(team.players, decodedName);
    if (player) {
      return { player, teamName: team.name };
    }
  }
  return undefined;
};

export const usePlayerPage = () => {
  const { playerName = "" } = useParams();
  const { pathname } = useLocation();
  const { strings } = useLocalization();
  const { internalPath } = useLocalePath();
  const copy = strings.PLAYER_PAGE;
  const teamInfos = useTeamInfo();
  const decodedPlayerName = decodeURIComponent(playerName);

  const rosterMatch = findRosterPlayerOnAnyTeam(teamInfos, decodedPlayerName);
  const rosterPlayer = rosterMatch?.player;

  const [seasonStats, setSeasonStats] = useState<IPlayerSeasonStats[]>([]);
  const [games, setGames] = useState<IPlayerGameStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [champStatsExpanded, setChampStatsExpanded] = useState(false);
  const mountedRef = useRef(true);

  const statLookupName = rosterPlayer?.name ?? decodedPlayerName;
  const lookupNames = useMemo(
    () => playerLookupNames(statLookupName, rosterPlayer?.displayName),
    [statLookupName, rosterPlayer?.displayName],
  );

  useEffect(() => {
    if (!decodedPlayerName.trim() || lookupNames.length === 0) {
      setLoading(false);
      return;
    }

    mountedRef.current = true;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const seasons = await fetchSeasons();
        const statsBySeason = await Promise.all(
          seasons.map((season) => fetchPlayerSeasonStats(season)),
        );
        const matched = findAllPlayerSeasonStats(
          statsBySeason.flat(),
          statLookupName,
          rosterPlayer?.displayName,
        );
        const gameRows = await fetchPlayerGameStats("", lookupNames);

        if (!mountedRef.current) return;
        setSeasonStats(matched);
        setGames(gameRows);
      } catch {
        if (mountedRef.current) setError("Pelaajan tietoja ei voitu ladata.");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [decodedPlayerName, statLookupName, rosterPlayer?.displayName, lookupNames.join("\0")]);

  useEffect(() => {
    setChampStatsExpanded(false);
  }, [decodedPlayerName]);

  const displayName = rosterPlayer?.name || decodedPlayerName;
  const teamName = rosterMatch?.teamName;
  const rankLabel = rosterPlayer ? rosterPlayer.rank.trim() : "";
  const hasRank = rankLabel.length > 0 && rankLabel.toLowerCase() !== "unranked";
  const tier = hasRank ? rankLabel.split(" ")[0].toLowerCase() : "unranked";
  const displayRank = hasRank ? rankLabel : "Unranked";
  const rankIconUrl = rankedCrestUrl(tier);
  const championStats = useMemo(() => aggregateChampionStats(games), [games]);
  const sortedGames = useMemo(() => sortGamesChronologically(games), [games]);
  const totalGamesPlayed = games.length;
  const uniqueChampionsPlayed = championStats.length;
  const hiddenChampionCount = Math.max(0, championStats.length - MOBILE_CHAMP_PREVIEW);
  const showChampToggle = championStats.length > MOBILE_CHAMP_PREVIEW;
  const showMultikillColumn = hasMultikillData(games);
  const hasContent = loading || rosterPlayer != null || seasonStats.length > 0 || games.length > 0;
  const showNotFound = !loading && !rosterPlayer && seasonStats.length === 0 && games.length === 0;
  const playerDescription = teamName
    ? `${displayName} (${teamName}). ${strings.SEO.PLAYER.DESCRIPTION}`
    : `${displayName}. ${strings.SEO.PLAYER.DESCRIPTION}`;

  return {
    copy,
    strings,
    pathname,
    internalPath,
    rosterPlayer,
    teamName,
    seasonStats,
    games,
    loading,
    error,
    champStatsExpanded,
    setChampStatsExpanded,
    displayName,
    hasRank,
    displayRank,
    rankIconUrl,
    championStats,
    sortedGames,
    totalGamesPlayed,
    uniqueChampionsPlayed,
    hiddenChampionCount,
    showChampToggle,
    showMultikillColumn,
    hasContent,
    showNotFound,
    playerDescription,
  };
};
